import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Show, SignInButton, UserButton } from "@clerk/react";
import { Menu, X, Bell, Sparkles } from "lucide-react";
import { useMe, useApi } from "../../lib/api";

const NAV = [["/", "Home"], ["/how-it-works", "How It Works"], ["/services", "Our Services"], ["/about", "About"], ["/contact", "Contact"]];

export const Logo = ({ className = "h-12" }) => (
  <img src="/brand/altiora-logo.png" alt="Altiora Ventures — Ideas | Insights | Solutions" className={`${className} w-auto object-contain`} data-testid="brand-logo" />
);

function NavItems({ onClick }) {
  return NAV.map(([to, label]) => (
    <NavLink key={to} to={to} end={to === "/"} onClick={onClick} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => `text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "text-[#0b3d2e]" : "text-slate-600 hover:text-[#0b3d2e]"}`}>
      {label}
    </NavLink>
  ));
}

// Always-visible AI image credit balance; one fetch per navigation, live updates via the 'altiora:credits' event.
function useCreditBalance(enabled) {
  const api = useApi();
  const { pathname } = useLocation();
  const [bal, setBal] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    api.get("/credits/wallet").then((r) => setBal(r.data.balance)).catch(() => setBal(null));
    const on = (e) => { if (typeof e.detail === "number") setBal(e.detail); else api.get("/credits/wallet").then((r) => setBal(r.data.balance)).catch(() => {}); };
    window.addEventListener("altiora:credits", on);
    return () => window.removeEventListener("altiora:credits", on);
  }, [api, pathname, enabled]);
  return enabled ? bal : null;
}

function CreditChip({ bal, testid, onClick }) {
  if (bal === null || bal === undefined) return null;
  return (
    <Link to="/dashboard/credits" onClick={onClick} title="AI image credits" data-testid={testid}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${bal < 2 ? "border-amber-300 bg-amber-50 text-[#7a5a15]" : "border-[#b8892b]/40 bg-[#fbf6ea] text-[#062a20] hover:border-[#b8892b]"}`}>
      <Sparkles className="w-3.5 h-3.5 text-[#b8892b]" /><span className="hidden sm:inline">AI credits:</span> <b className="tabular-nums" data-testid={`${testid}-value`}>{bal}</b>
    </Link>);
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { me } = useMe();
  const bal = useCreditBalance(!!me?.credits);
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#0b3d2e]/10" data-testid="site-header">
      <div className="max-w-7xl 2xl:max-w-[1440px] mx-auto px-5 lg:px-8 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="shrink-0" data-testid="header-home-link" aria-label="Altiora Ventures home"><Logo className="h-14" /></Link>
        <nav className="hidden lg:flex items-center gap-4 xl:gap-6" aria-label="Main"><NavItems /></nav>
        <div className="hidden lg:flex items-center gap-3 whitespace-nowrap shrink-0">
          <Show when="signed-in">
            {me && ["admin", "staff"].includes(me.role) && <Link to="/admin" className="text-sm font-medium text-[#9a7322]" data-testid="nav-admin">Admin</Link>}
            <Link to="/presentations" className="hidden 2xl:inline text-sm font-medium text-slate-700" data-testid="nav-presentations">My presentations</Link>
            <Link to="/dashboard" className="text-sm font-medium text-slate-700 inline-flex items-center gap-1" data-testid="nav-dashboard"><Bell className="w-4 h-4" />My projects</Link>
            {me?.credits && <CreditChip bal={bal} testid="nav-credit-balance" />}
            <UserButton />
          </Show>
          <Show when="signed-out"><Link to="/sign-in" className="text-sm font-medium text-slate-700" data-testid="nav-sign-in">Sign in</Link></Show>
          <span className={me ? "hidden xl:contents" : "contents"} data-testid="header-ctas">
            <Link to="/presentations" className="btn-gold !py-2.5 !px-4" data-testid="header-pres-cta">Build Your Presentation</Link>
            <Link to="/build" className="btn-emerald !py-2.5 !px-4" data-testid="header-build-cta">Build Your Website</Link>
          </span>
        </div>
        <div className="lg:hidden flex items-center gap-2">
          <Show when="signed-in">{me?.credits && <CreditChip bal={bal} testid="mobile-credit-balance" />}</Show>
          <button className="p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t bg-white px-5 py-5 flex flex-col gap-4" data-testid="mobile-menu">
          <NavItems onClick={() => setOpen(false)} />
          <Show when="signed-in">
            <Link to="/presentations" onClick={() => setOpen(false)} className="text-sm font-medium" data-testid="mobile-nav-presentations">My presentations</Link>
            <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm font-medium">My projects</Link>
            {me && ["admin", "staff"].includes(me.role) && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-medium text-[#9a7322]">Admin</Link>}
            <UserButton />
          </Show>
          <Show when="signed-out"><Link to="/sign-in" onClick={() => setOpen(false)} className="text-sm font-medium">Sign in</Link></Show>
          <Link to="/presentations" onClick={() => setOpen(false)} className="btn-gold" data-testid="mobile-pres-cta">Build Your Presentation</Link>
          <Link to="/build" onClick={() => setOpen(false)} className="btn-emerald" data-testid="mobile-build-cta">Build Your Website</Link>
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="surface-emerald grain mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2 space-y-4">
          <div className="bg-white inline-block rounded-md p-3"><Logo className="h-16" /></div>
          <p className="text-sm text-[#cfc6ad] max-w-sm">Business solutions across research, analysis, documentation, presentations, websites, mobile applications and AI.</p>
          <a href="mailto:hello@altiora.ventures" className="text-[#d9b44c] text-sm" data-testid="footer-email">hello@altiora.ventures</a>
        </div>
        <div className="space-y-2 text-sm">
          <p className="eyebrow !text-[#d9b44c]">Company</p>
          {[["/about", "About"], ["/services", "Our Services"], ["/website-development", "Website Development"], ["/how-it-works", "How It Works"], ["/faqs", "FAQs"], ["/contact", "Contact"]].map(([t, l]) => (
            <Link key={t} to={t} className="block text-[#e9dfc4] hover:text-white">{l}</Link>))}
        </div>
        <div className="space-y-2 text-sm">
          <p className="eyebrow !text-[#d9b44c]">Policies</p>
          {[["privacy", "Privacy Policy"], ["terms", "Terms and Conditions"], ["refunds", "Refund & Cancellation"], ["cookies", "Cookie Policy"], ["disclaimer", "AI & Content Disclaimer"]].map(([t, l]) => (
            <Link key={t} to={`/legal/${t}`} className="block text-[#e9dfc4] hover:text-white" data-testid={`footer-legal-${t}`}>{l}</Link>))}
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-[#a99f86]">© {new Date().getFullYear()} Altiora Ventures · www.altiora.ventures</div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (<><SiteHeader /><main className="min-h-[60vh]">{children}</main><SiteFooter /></>);
}

export function PageHero({ eyebrow, title, text }) {
  return (
    <section className="surface-emerald grain">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-24 fade-up">
        <p className="eyebrow !text-[#d9b44c]">{eyebrow}</p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#f6ecd2] max-w-3xl leading-[1.05]">{title}</h1>
        {text && <p className="mt-6 text-base md:text-lg text-[#cfc6ad] max-w-2xl">{text}</p>}
      </div>
    </section>
  );
}
